import React, { useMemo } from 'react';
import NextLink from 'next/link';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Paper,
  Divider,
  Tooltip,
  LinearProgress,
  Link,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { alpha, useTheme } from '@mui/material/styles';
import { getCommonStyles, getTaskVisuals, getUiStyles, getVersionInfo, onboardingActionButtonSx } from './constants';
import type { HarborOnboardingVersion, OnboardingTask } from './onboarding';

type OnboardingPopupProps = {
  currentVersion: HarborOnboardingVersion;
  tasks: OnboardingTask[];
  onCloseClick: () => void;
  onTaskClick: (taskId: string) => void;
  onBackToPreviousPart: () => void;
  onGoToNextPart: () => void;
};

const partLabel = (version: HarborOnboardingVersion): string =>
  version === 'harbor101' ? 'Part 1' : version === 'harbor102' ? 'Part 2' : 'Part 3';

const OnboardingPopup = ({
  currentVersion,
  tasks,
  onCloseClick,
  onTaskClick,
  onBackToPreviousPart,
  onGoToNextPart,
}: OnboardingPopupProps) => {
  const theme = useTheme();
  const commonStyles = useMemo(() => getCommonStyles(theme), [theme]);
  const uiStyles = useMemo(() => getUiStyles(theme), [theme]);
  const versionInfo = getVersionInfo(currentVersion);

  const incompleteTasksCount = tasks.filter(t => !t.completed).length;
  const allTasksCompleted = tasks.every(t => t.completed);
  const progressPercentage =
    tasks.length === 0 ? 0 : Math.round(((tasks.length - incompleteTasksCount) / tasks.length) * 100);

  const currentPartLabel = partLabel(currentVersion);
  const title = versionInfo.title;
  const strikeColor = alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.45 : 0.35);

  return (
    <Paper elevation={8} sx={uiStyles.paper}>
      <Box sx={commonStyles.gradientHeader}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: 1, mb: 1.5 }}>
          <Box sx={{ minWidth: 0, pr: 1 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, letterSpacing: '0.5px', fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              {title}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: alpha(theme.palette.primary.contrastText, 0.88),
                display: 'block',
                mt: 0.25,
              }}
            >
              <Box component="span" sx={{ display: 'inline-block', mr: 0.625 }}>
                {progressPercentage}% completed{' '}
              </Box>
              |
              <Box component="span" sx={{ display: 'inline-block', ml: 0.625 }}>
                {incompleteTasksCount === 0
                  ? 'All tasks completed!'
                  : `${incompleteTasksCount} task${incompleteTasksCount !== 1 ? 's' : ''} remaining`}
              </Box>
            </Typography>
          </Box>
          <Tooltip title="Close guide">
            <IconButton
              size="small"
              data-testid="@onboarding-close-guide"
              onClick={onCloseClick}
              sx={{
                color: 'inherit',
                flexShrink: 0,
                transition: 'all 0.2s',
                '&:hover': { transform: 'scale(1.2)' },
              }}
            >
              <CloseIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
            </IconButton>
          </Tooltip>
        </Box>
        <LinearProgress
          color="secondary"
          variant="determinate"
          value={progressPercentage}
          sx={{
            alignSelf: 'stretch',
            mx: { xs: -2, sm: -2.5 },
            height: 6,
            borderRadius: 0,
            backgroundColor: alpha(theme.palette.primary.contrastText, 0.15),
            '& .MuiLinearProgress-bar': {
              borderRadius: 0,
              backgroundColor: alpha(theme.palette.primary.contrastText, 0.92),
            },
          }}
        />
      </Box>

      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          bgcolor: theme =>
            theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.14) : theme.palette.background.default,
          overflow: 'auto',
          flexGrow: 1,
          WebkitOverflowScrolling: 'touch',
          boxShadow: theme =>
            theme.palette.mode === 'dark' ? `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.06)}` : undefined,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            mb: 1.5,
          }}
        >
          <Typography
            variant="body1"
            component="span"
            sx={{ whiteSpace: 'nowrap', color: 'text.primary', fontWeight: 600 }}
          >
            <Link
              component={NextLink}
              href="/settings/migrate-data"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              variant="body1"
              data-testid="@onboarding-import-data"
              sx={{ color: 'text.secondary', fontWeight: 400, ml: 0.625 }}
            >
              Import Data
            </Link>
          </Typography>
        </Box>

        <Typography
          variant="body1"
          sx={{
            mb: 2,
            fontWeight: 500,
            fontSize: { xs: '0.9rem', sm: '1rem' },
            lineHeight: 1.55,
            color: 'text.primary',
          }}
        >
          {allTasksCompleted && currentVersion !== 'harbor103'
            ? `🎉 Congratulations! You've completed ${currentPartLabel}.`
            : versionInfo.description}
        </Typography>

        <List sx={{ mb: 2 }}>
          {tasks.map(task => {
            const isCompleted = task.completed;
            const visuals = getTaskVisuals(theme, isCompleted, true, false);

            return (
              <ListItem
                key={task.id}
                sx={{
                  bgcolor: visuals.bg,
                  borderRadius: 2,
                  mb: 1,
                  border: 1,
                  borderColor: visuals.border,
                  py: { xs: 0.8, sm: 1.2 },
                  px: { xs: 1.5, sm: 2 },
                  cursor: 'pointer',
                }}
                onClick={() => onTaskClick(task.id)}
              >
                <ListItemIcon
                  sx={{
                    minWidth: { xs: '36px', sm: '42px' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {isCompleted ? (
                    <CheckCircleOutlineIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: visuals.icon }} />
                  ) : (
                    <RadioButtonUncheckedIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, color: visuals.icon }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={task.title}
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontWeight: 500,
                      fontSize: { xs: '0.85rem', sm: '0.95rem' },
                      color: visuals.text,
                      textDecoration: isCompleted ? 'line-through' : 'none',
                      textDecorationColor: isCompleted ? strikeColor : undefined,
                      textDecorationThickness: isCompleted ? '1px' : undefined,
                    },
                  }}
                />
              </ListItem>
            );
          })}
        </List>

        <Divider sx={{ my: { xs: 1.5, sm: 2 }, borderColor: 'divider' }} />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: { xs: 1.5, sm: 2 },
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            gap: { xs: 1, sm: 0 },
          }}
        >
          {currentVersion !== 'harbor101' ? (
            <Button variant="contained" color="inherit" onClick={onBackToPreviousPart} sx={onboardingActionButtonSx}>
              Back to Part {currentVersion === 'harbor103' ? '2' : '1'}
            </Button>
          ) : (
            <Box />
          )}
          <Box>
            {currentVersion !== 'harbor103' && (
              <Button variant="contained" color="primary" onClick={onGoToNextPart} sx={onboardingActionButtonSx}>
                Go to Part {currentVersion === 'harbor101' ? '2' : '3'}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default OnboardingPopup;
